import { useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { tpoGenUrls } from 'config/urls';
import { tpoGenKeys } from '../../../../utils/tpoGenQueryKeys';
import { sendNotification } from 'utils/helperMethods';
import {
    TPOGEN_IMPORT_EVENT_COMPLETED,
    TPOGEN_IMPORT_EVENT_FAILED,
} from 'utils/mixpanelEventNames';
import { useEditWriteGuard } from '../../../EditSession/useEditWriteGuard';
import { trackTpoGenEvent, TPOGEN_PAGE_NAMES } from '../../../../utils/trackTpoGenEvent';

interface ImportEventsPayload {
    sourcePlanId: string;
    targetPlanId: string;
    promoIds: string[];
}

interface ImportedMapping {
    source_event_id?: string;
    new_event_id?: string;
    source_promo_id?: string;
    new_promo_id?: string;
}

interface ImportEventsResponse {
    status: string;
    imported_events: ImportedMapping[];
    imported_promos: ImportedMapping[];
    warnings: string[];
}

const executeImport = async (payload: {
    user_id: string;
    source_plan_id: string;
    target_plan_id: string;
    promo_ids: string[];
}): Promise<ImportEventsResponse> => {
    const token = localStorage.getItem('accessToken');
    const response = await axios(tpoGenUrls.importEvents, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        data: payload,
    });
    return response.data;
};

const useImportEventsMutation = ({ onSuccess }: { onSuccess?: (data: ImportEventsResponse) => void } = {}) => {
    const queryClient = useQueryClient();
    const assertCanWrite = useEditWriteGuard();
    const startedAtRef = useRef(0);

    const { mutate, isPending } = useMutation({
        mutationFn: ({ sourcePlanId, targetPlanId, promoIds }: ImportEventsPayload) => {
            assertCanWrite();
            startedAtRef.current = performance.now();
            const userId = localStorage.getItem('email') ?? '';
            return executeImport({
                user_id: userId,
                source_plan_id: sourcePlanId,
                target_plan_id: targetPlanId,
                promo_ids: promoIds,
            });
        },
        onSuccess: (data, variables) => {
            queryClient.removeQueries({ queryKey: [...tpoGenKeys.all, 'planning-groups'] });
            queryClient.removeQueries({ queryKey: [...tpoGenKeys.all, 'plan-list'] });
            queryClient.invalidateQueries({ queryKey: ['calendarData'] });
            const promoCount = data.imported_promos?.length ?? 0;
            sendNotification('success', `Successfully imported ${promoCount} promo(s)`);
            if (data.warnings?.length) {
                sendNotification('info', data.warnings.join(' \u2022 '));
            }
            trackTpoGenEvent(TPOGEN_IMPORT_EVENT_COMPLETED, TPOGEN_PAGE_NAMES.PLAN_DETAIL_CALENDAR, {
                source_plan_id: variables.sourcePlanId,
                target_plan_id: variables.targetPlanId,
                promo_count: promoCount,
                // `product_count` is omitted: the import API returns only event/promo
                // mappings, not a product-level count (see ImportEventsResponse).
                duration_ms: Math.round(performance.now() - startedAtRef.current),
            });
            onSuccess?.(data);
        },
        onError: (error: any, variables) => {
            const message =
                error?.response?.data?.detail ||
                error?.response?.data?.message ||
                error?.message ||
                'Failed to import events';
            sendNotification('error', message);
            trackTpoGenEvent(TPOGEN_IMPORT_EVENT_FAILED, TPOGEN_PAGE_NAMES.PLAN_DETAIL_CALENDAR, {
                source_plan_id: variables.sourcePlanId,
                target_plan_id: variables.targetPlanId,
                failure_reason: message,
                duration_ms: Math.round(performance.now() - startedAtRef.current),
            });
        },
    });

    return { mutate, isPending };
};

export default useImportEventsMutation;
